import type { ChangeEvent, ComponentClass, ComponentType, FocusEvent, LegacyRef } from "react";

import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import type { IComponent } from "@Components/Component";
import type { IInput } from "@Components/Input";
import type { ITextbox } from "@Components/Textbox";
import { Textbox } from "@Components/Textbox";

export interface IInputTracker<TProps extends IInput> extends IComponent, ITrackerActions {
    properties: TProps;
    ref?: LegacyRef<ComponentType<TProps>>;
}

export interface ITrackerActions {
    blurTelemetryCustom?: ITelemetryCustom;
    changeTelemetryCustom?: ITelemetryCustom;
    focusTelemetryCustom?: ITelemetryCustom;
}

export function SetInputTrackerProps<TProps extends IInput = IInput>(props: IInputTracker<TProps>): TProps {
    const onBlur: typeof props.properties.onBlur = (...args) => {
        sendTelemetryEvent(props.blurTelemetryCustom);

        props.properties.onBlur?.(...args);
    };

    const onChange = (event: ChangeEvent<HTMLElement>): void => {
        sendTelemetryEvent(props.changeTelemetryCustom);

        props.properties.onChange?.(event);
    };

    const onFocus = (event: FocusEvent<HTMLElement>): void => {
        sendTelemetryEvent(props.focusTelemetryCustom);

        props.properties.onFocus?.(event);
    };

    return {
        ...props.properties,
        onBlur,
        onChange,
        onFocus,
    };
}

function InputTrackerHOC<TProps extends IInput>(
    InputComponent: ComponentClass<TProps>,
): React.FC<IInputTracker<TProps>> {
    return function (props: IInputTracker<TProps>) {
        if (!props || !props.properties) {
            return null;
        }

        const componentProps = SetInputTrackerProps(props);

        return <InputComponent {...componentProps} />;
    };
}

export const TextboxTracker = InputTrackerHOC<ITextbox>(Textbox);
